import React from "react";
import { makeStyles, Typography } from "@material-ui/core";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl";
import Number from "../../../../custom/Number";
import MonthlyIncomeTable from "./MonthlyIncomeTable";
import LoadingSpinner from "../../LoadingSpinner";
import ContractsWithEarningsByYear from "./ContractsWithEarningsByYear";
import ErrorMessage from "../../ErrorMessage";
import { resolveErrorMessage } from "../../../../util";
import AnimatedCardContent from "../../AnimatedCardContent";

const useStyles = makeStyles((theme) => ({
  title: {
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(2),
  },
}));

const ContractEarnings = ({ currentUser, user, year, type, getEarnings }) => {
  const classes = useStyles();
  const [earnings, setEarnings] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const params = {
          contractTypeType: type,
          statementOrigin: "PROFIT",
          year: year,
        };
        const data = await getEarnings(params);
        if (data.capitalEarning <= 0) {
          throw new Error("app.earnings.noEarningsReport");
        }
        setEarnings(data);
      } catch (e) {
        const errorMessage = resolveErrorMessage(e);
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getEarnings, type, year]);

  if (loading)
    return (
      <LoadingSpinner
        marginTop={20}
        message={<FormattedMessage id="app.loading.fetching.earnings" />}
      />
    );

  if (error)
    return (
      <ErrorMessage>
        <FormattedHTMLMessage
          id={error}
          values={{ year }}
        />
      </ErrorMessage>
    );

  return (
    <>
      <AnimatedCardContent show={!loading}>
        <Typography
          gutterBottom
          variant="h6"
          color="primary"
          className={classes.title}
        >
          <FormattedHTMLMessage
            id="app.earnings.description"
            values={{ year }}
          />
        </Typography>

        <Typography
          gutterBottom
          variant="body2"
        >
          <FormattedHTMLMessage id="app.earnings.custodiedValue" />
          <Number
            currency="LCT"
            hideValue={currentUser.hideValues}
            taxResidence={user.taxResidence}
            value={earnings?.capitalInvested || 0}
          />
        </Typography>
        <Typography
          gutterBottom
          variant="body2"
        >
          <FormattedHTMLMessage id="app.earnings.periodEarnings" />
          <Number
            currency="LCT"
            hideValue={currentUser.hideValues}
            taxResidence={user.taxResidence}
            value={earnings?.capitalEarning || 0}
          />
        </Typography>
      </AnimatedCardContent>

      <AnimatedCardContent>
        <MonthlyIncomeTable
          currentUser={currentUser}
          data={earnings?.monthlyEarnings}
          user={user}
          year={year}
          message="app.earnings.income.description"
        />
      </AnimatedCardContent>

      <AnimatedCardContent>
        <ContractsWithEarningsByYear
          currentUser={currentUser}
          data={earnings?.contracts}
          user={user}
          year={year}
        />
      </AnimatedCardContent>
    </>
  );
};

export default ContractEarnings;
